"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { Loader } from "lucide-react";
export default function GetStarted() {
  return <section id="get-started" className="relative py-24 bg-dark-background overflow-hidden">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-12 space-y-6">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">
            Get Started Today
          </h2>
          <p className="text-lg text-white/80 max-w-xl mx-auto">
            Join our network of trusted suppliers and buyers. Connect with global trade opportunities and grow your business.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
            <Link to="/supplier-portal">
              <Button className="btn-primary w-full sm:w-auto min-w-[200px] hover:-translate-y-0.5 hover:shadow-medium transition-all duration-[var(--duration-fast)]">
                Supplier Portal
              </Button>
            </Link>
            <Link to="/buyer-portal">
              <Button className="btn-primary w-full sm:w-auto min-w-[200px] hover:-translate-y-0.5 hover:shadow-medium transition-all duration-[var(--duration-fast)]">
                Buyer Portal
              </Button>
            </Link>
          </div>
        </div>

        <FormProvider className="mt-16" formId="get_started_form_supplierportalandbuyerportal" sectionName="get-started" defaultValues={{
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        role: "",
        interested_in: ""
      }} validationRules={{
        company_name: {
          required: true
        },
        contact_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        role: {
          required: true
        },
        interested_in: {
          required: false
        }
      }} submitText="Submit Registration" successMessage="Thank you! We'll contact you shortly to complete your registration." errorMessage="Unable to submit your registration. Please try again.">
          <GetStartedForm />
        </FormProvider>
      </div>
    </section>;
}
function GetStartedForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-accent/10 border-2 border-accent rounded-sm text-center">
        <p className="text-white text-lg mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="text-white border-white/30 hover:bg-white/10">
          Register Another Business
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="p-8 bg-secondary/40 backdrop-blur-sm border border-white/10 rounded-sm space-y-6">
        <h3 className="font-display text-2xl font-bold text-white mb-6 text-center">
          Get Started Form
        </h3>

        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Trading Co." className="bg-white/10 border-secondary text-white placeholder:text-white/50 focus:border-accent focus:border-2" {...field} />
              </FormControl>
              <p className="text-white/60 text-xs mt-1">Enter the legal name of your business.</p>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact Person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-white/10 border-secondary text-white placeholder:text-white/50 focus:border-accent focus:border-2" {...field} />
              </FormControl>
              <p className="text-white/60 text-xs mt-1">Full name of the primary contact.</p>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john.doe@example.com" className="bg-white/10 border-secondary text-white placeholder:text-white/50 focus:border-accent focus:border-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+971 55 123 4567" className="bg-white/10 border-secondary text-white placeholder:text-white/50 focus:border-accent focus:border-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" rules={{
        required: "Please select your role"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">I am a</FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="bg-white/10 border-secondary text-white focus:border-accent focus:border-2">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supplier">Supplier</SelectItem>
                    <SelectItem value="Buyer">Buyer</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="interested_in" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white">Products / Services of Interest</FormLabel>
              <FormControl>
                <Textarea placeholder="e.g., grains, spices, logistics services..." className="bg-white/10 border-secondary text-white placeholder:text-white/50 focus:border-accent focus:border-2 min-h-[100px]" {...field} />
              </FormControl>
              <p className="text-white/60 text-xs mt-1">
                Briefly describe the commodities or services you are looking to source or offer.
              </p>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-sm">
            <p className="text-red-200">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary mt-6">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : "Submit Registration"}
        </Button>
      </div>
    </Form>;
}